import { TFunction } from "react-i18next";

import { Branch } from "../../../../../../../../models/branch";
import { StorageBoxType } from "../../../../../../../../models/storage.box.type";
import { Warehouse } from "../../../../../../../../models/warehouse";

export interface StorageBoxTemplateCSV {
  number: string;
  storageBoxType: string;
  branch: string;
  sharedWarehouses: string;
  belongsToWarehouse: string;
}

export interface ValidateCSVFieldErrors {
  [line: string]: string[];
}

export type Translate = TFunction<"translation", undefined>;

export interface Props {
  branches: Branch[];
  warehouses: Warehouse[];
  storageBoxTypes: StorageBoxType[];
  data: StorageBoxTemplateCSV[];
  translate: Translate;
  lineErrors: ValidateCSVFieldErrors;
}

export const getStorageBoxTemplateColumnName = (translate: Translate) => {
  return {
    number: translate("number"),
    storageBoxType: translate("type"),
    branch: translate("branch"),
    sharedWarehouses: translate("sharedWarehouses"),
    belongsToWarehouse: translate("belongsToWarehouse"),
  };
};

export const ValidateStorageBoxesCSV = ({
  branches,
  warehouses,
  storageBoxTypes,
  data,
  translate,
  lineErrors,
}: Props) => {
  const handleGetBranchesNames = (branches: Branch[]) => {
    // return branches.map((branch) => branch.name);
    return (branches || []).reduce((prev, curr) => {
      if (curr?.name) {
        prev.push(curr.name)
      }
      if (curr?.code) {
        prev.push(curr.code)
      }
      return prev;
    },
      [] as string[]
    );
  };

  const handleGetWarehousesNames = (warehouses: Warehouse[], branchName?: string) => {
    let warehousesFiltered: Warehouse[] = warehouses;

    if (branchName) {
      const branch = branches?.find(
        (branch) => branch.name?.toLowerCase() === branchName?.toLowerCase() || branch?.code?.toLowerCase() === branchName?.toLowerCase()
      );

      if (branch && branch?.warehouses?.length) warehousesFiltered = branch?.warehouses;
    }

    // return warehousesFiltered.map((warehouse) => warehouse.name);
    return (warehousesFiltered || []).reduce((prev, curr) => {
      if (curr.name) {
        prev.push(curr.name)
      }
      if (curr.code) {
        prev.push(curr.code)
      }
      return prev;
    },
      [] as string[]
    );
  };

  const handleGetStorageBoxTypesNames = (storageBoxTypes: StorageBoxType[]) => {
    return storageBoxTypes.map((storageBoxType) => storageBoxType.name);
  };

  const getRequiredMessageError = (columnName: string, translate: Translate) =>
    `${columnName} ${translate("isRequired")}`;

  const getNotValidMessageErrorExpected = (
    columnName: string,
    value: string,
    expectedValues: Array<string | undefined>
  ) =>
    `${columnName} (${value}) ${translate("canBeJust")} ${expectedValues.join(
      ` ${translate("or")} `
    )}`;

  const branchWasNoWarehouses = (columnName: string, value: string, translate: Translate) =>
    `${columnName}: ${value} ${translate("hasNoWarehouses")}`;

  const storageBoxTypeNotIsShareable = (columnName: string, value: string, translate: Translate) =>
    `${columnName}: ${value} ${translate("isNotShareable")}`;

  const messages = (translate: Translate) => {
    return {
      required: (columnName: string) => getRequiredMessageError(columnName, translate),
      expectedField: (
        columnName: string,
        value: string,
        expectedValue: Array<string | undefined>
      ) => getNotValidMessageErrorExpected(columnName, value, expectedValue),
      unexpectedField: (
        columnName: string,
        value: string,
        callback: (columnName: string, value: string, translate: Translate) => string
      ) => callback(columnName, value, translate),
    };
  };

  const changeLineError = (
    lineErrors: ValidateCSVFieldErrors,
    line: number,
    messageError: string
  ) => {
    if (lineErrors[line]) {
      lineErrors[line] = [...lineErrors[line], messageError];
    } else {
      lineErrors[line] = [messageError];
    }
  };

  const addErrorInLine = (
    lineErrors: ValidateCSVFieldErrors,
    line: number,
    messageError: string,
    callback: () => void
  ) => {
    changeLineError(lineErrors, line, messageError);
    callback();
  };

  const validateStorageBox = (storageBox: StorageBoxTemplateCSV, line: number) => {
    const branchesNames = handleGetBranchesNames(branches);
    const warehousesNames = handleGetWarehousesNames(warehouses, storageBox?.branch);
    const storageBoxTypesNames = handleGetStorageBoxTypesNames(storageBoxTypes);
    const message = messages(translate);
    const getStorageBoxColumnName = getStorageBoxTemplateColumnName(translate);
    let isValid = true;

    const addError = (messageError: string) => {
      addErrorInLine(lineErrors, line, translate(messageError), () => {
        isValid = false;
      });
    };

    if (!storageBox.number) addError(message.required(getStorageBoxColumnName.number));

    // if (storageBox.branch && !branchesNames.includes(storageBox.branch)) {
    if (storageBox.branch && !branchesNames?.some((branchName) => branchName?.toLowerCase() === storageBox?.branch?.toLowerCase())) {
      addError(
        message.expectedField(getStorageBoxColumnName.branch, storageBox.branch, branchesNames)
      );
    }

    if (storageBox?.branch && !warehousesNames?.length) {
      addError(
        message.unexpectedField(
          getStorageBoxColumnName.branch,
          storageBox?.branch,
          branchWasNoWarehouses
        )
      );
    }

    if (!storageBox.storageBoxType) {
      addError(message.required(getStorageBoxColumnName.storageBoxType));
    }

    // if (storageBox.storageBoxType && !storageBoxTypesNames.includes(storageBox.storageBoxType)) {
    if (storageBox.storageBoxType && !storageBoxTypesNames?.some((typeName) => typeName?.toLowerCase() === storageBox?.storageBoxType?.toLowerCase())) {
      addError(
        message.expectedField(
          getStorageBoxColumnName.storageBoxType,
          storageBox.storageBoxType,
          storageBoxTypesNames
        )
      );
    }

    if (storageBox?.storageBoxType) {
      const storageBoxType = storageBoxTypes?.find(
        (storageBoxType) => storageBoxType.name === storageBox?.storageBoxType
      );

      if (
        storageBoxType &&
        !storageBoxType?.isShareable &&
        storageBox.sharedWarehouses &&
        storageBox.sharedWarehouses?.split("|")?.length > 0
      ) {
        addError(
          message.unexpectedField(
            getStorageBoxColumnName.storageBoxType,
            storageBox?.storageBoxType,
            storageBoxTypeNotIsShareable
          )
        );
      }
    }

    if (!storageBox.belongsToWarehouse)
      addError(message.required(getStorageBoxColumnName.belongsToWarehouse));

    // if (storageBox?.belongsToWarehouse && !warehousesNames.includes(storageBox?.belongsToWarehouse)) {
    if (storageBox?.belongsToWarehouse && !warehousesNames?.some((warehouseName) => warehouseName?.toLowerCase() === storageBox?.belongsToWarehouse?.toLowerCase())) {
      addError(
        message.expectedField(
          getStorageBoxColumnName.belongsToWarehouse,
          storageBox?.belongsToWarehouse,
          warehousesNames
        )
      );
    }

    if (storageBox?.sharedWarehouses && storageBox?.sharedWarehouses?.split("|")?.length > 0) {
      const storageBoxType = storageBoxTypes?.find(
        (storageBoxType) => storageBoxType.name === storageBox?.storageBoxType
      );

      if (storageBoxType?.isShareable) {
        storageBox.sharedWarehouses.split("|").forEach((warehouse) => {
          if (!warehousesNames.includes(warehouse.trim())) {
            addError(
              message.expectedField(
                getStorageBoxColumnName.sharedWarehouses,
                warehouse,
                warehousesNames
              )
            );
          }
        });
      }
    }

    return isValid;
  };

  const validateStorageBoxes = (data: StorageBoxTemplateCSV[]) => {
    const areValid = new Array(data.length).fill(true, 0);

    data.forEach((storageBox, index) => {
      areValid[index] = validateStorageBox(storageBox, index + 1);
    });

    return {
      valid: areValid.every((valid) => valid),
    };
  };

  return validateStorageBoxes(data);
};
